import { useState, useEffect, useCallback, forwardRef, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";

import {
	DataGrid,
	Toolbar,
	ToolbarButton,
	QuickFilter,
	QuickFilterControl,
	QuickFilterClear,
	QuickFilterTrigger,
} from "@mui/x-data-grid";
import clsx from "clsx";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import {
	TextField,
	Typography,
	Accordion,
	AccordionDetails,
	AccordionSummary,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAtom, useAtomValue } from "jotai";
import { examProcessingStateAtom, classListFileAtom } from "../../../components/Atoms/atoms";

export default function UnmatchedStudentsDialog({
	open,
	setOpen,
	handleClose,
	unmatchedStudents,
	setUnmatchedStudents,
	handleUpdateStudentsInDB,
	loadingUpdateStudentRecords,
	exportLogsOnFinish,
	handleWrongClassId,
}) {
	const AUTO_COMMIT_INITIAL = true;
	const classListFile = useAtomValue(classListFileAtom);
	const [finishedMatching, setFinishedMatching] = useState(false);
	const [studentsYetToMatch, setStudentsYetToMatch] = useState([]);
	const [autoState, setAutoState] = useState({});
	const [pendingAssignments, setPendingAssignments] = useState({});
	const [examProcessingState, setExamProcessingState] = useAtom(examProcessingStateAtom);

	const [gotInitialMatches, setGotInitialMatches] = useState(false);
	const [stagedCannotMatch, setStagedCannotMatch] = useState({});
	const [exportCSVLog, setExportCSVLog] = useState(false);
	const [exportTXTLog, setExportTXTLog] = useState(true);

	const [canApplyChanges, setCanApplyChanges] = useState(false);

	useEffect(() => {
		if (unmatchedStudents?.length > 0) {
			const pendingAssignmentsCount = Object.keys(pendingAssignments).length;
			const stagedCannotMatchCount = Object.keys(stagedCannotMatch).length;
			const totalDecided = pendingAssignmentsCount + stagedCannotMatchCount;
			//same number
			setCanApplyChanges(totalDecided === unmatchedStudents.length);
		}
	}, [pendingAssignments, stagedCannotMatch, unmatchedStudents]);

	const hasUnmatched = useMemo(
		() =>
			(examProcessingState?.students.length > 0 ? examProcessingState.students : []).some(
				(r) => !r.matched,
			),
		[examProcessingState],
	);

	// 2) control which accordion is open: true = "Unmatched" open, false = "Matched" open
	const [isExpanded, setIsExpanded] = useState(false);

	// 3) set initial accordion state whenever the dialog opens or data changes
	useEffect(() => {
		if (open) setIsExpanded(hasUnmatched);
	}, [open, hasUnmatched]);

	const getRowAuto = (id) => autoState[id] ?? { value: null, input: "", match_type: null };
	const setRowAuto = (id, patch) =>
		setAutoState((prev) => ({
			...prev,
			[id]: { ...getRowAuto(id), ...patch },
		}));

	const hasStagedChanges = Object.keys(pendingAssignments).length > 0;
	const hasStagedCannotMatch = Object.keys(stagedCannotMatch).length > 0;

	const hasData = useMemo(
		() => Boolean(examProcessingState?.students?.length && classListFile?.classlist?.length),
		[examProcessingState, classListFile],
	);

	const chosenIds = useMemo(
		() => new Set(Object.values(pendingAssignments).filter(Boolean).map(String)),
		[pendingAssignments],
	);

	const returnLastFirstID = (last, first, id) => {
		return `${last}, ${first} (${id})`;
	};

	const extractId = (label) => String(label ?? "").match(/\((\d+)\)\s*$/)?.[1] ?? null;
	const normNamePiece = (s = "") => s.toLowerCase().trim().replace(/\s+/g, " ");
	const tokens = (s = "") => normNamePiece(s).split(/\s+/).filter(Boolean);
	const tokenSetHas = (setArr, needle) => setArr.includes(normNamePiece(needle));
	const idKey = (value = "") => {
		const digits = String(value ?? "").replace(/\D+/g, "");
		return digits ? digits.padStart(7, "0").slice(-7) : "";
	};
	const nameKey = (value = "") => String(value ?? "").toLowerCase().replace(/[\s,.-]/g, "").trim();

	useEffect(() => {
		if (
			examProcessingState &&
			examProcessingState.students.length > 0 &&
			classListFile &&
			classListFile.classlist.length > 0
		) {
			const matchedStudents = examProcessingState.students.filter(
				(student) => student.matched === true,
			);
			const classlistWithoutMatched = classListFile.classlist.filter(
				(enrollment) =>
					!matchedStudents.some(
						(student) => Number(student.std_id_matched) === Number(enrollment[6]),
					),
			);
			setStudentsYetToMatch(classlistWithoutMatched); // Remove matched students from list for matching to ensure no overwrites happen
		}
	}, [examProcessingState, classListFile]);

	useEffect(() => {
		if (!open) {
			setGotInitialMatches(false);
			setAutoState({});
			setPendingAssignments({});
			setStagedCannotMatch({});
			setExportCSVLog(false);
			setExportTXTLog(false);
		}
	}, [open]);

	const scoreCandidate = (row, classRow) => {
		const rawId = String(row.std_id_raw ?? "");
		const candidateId = String(classRow[6] ?? "");
		const nameRaw = String(row.std_name_raw ?? "");
		const first = String(classRow[3] ?? "");
		const last = String(classRow[4] ?? "");

		if ((!rawId && !nameRaw) || (rawId.trim().length === 0 && nameRaw.trim().length === 0))
			return { score: -999, badge: "" };

		const digits = (s = "") => s.replace(/\D+/g, "");
		const idExact = digits(rawId).padStart(7, "0") === digits(candidateId).padStart(7, "0");

		const squish = (s = "") => s.toLowerCase().replace(/[\s\-.]/g, "");
		const sRaw = squish(nameRaw);
		const sLF = squish(`${last}${first}`);
		const sFL = squish(`${first}${last}`);

		const nameExact = sRaw === sLF || sRaw === sFL;
		const nameClose =
			sRaw.startsWith(sLF) || sLF.startsWith(sRaw) || sRaw.startsWith(sFL) || sFL.startsWith(sRaw);

		// weights: tweak to taste
		if (idExact && nameExact) return { score: 100, badge: "Exact ID + Name" };
		if (idExact && nameClose) return { score: 92, badge: "Exact ID • Partial Name" };
		if (idExact) return { score: 85, badge: "Exact ID" };
		if (nameExact) return { score: 70, badge: "Exact Name" };
		if (nameClose) {
			return { score: 55, badge: "Similar Name" };
		}
		return { score: 0, badge: "" };
	};

	const handleMatchStudents = useCallback(async () => {
		if (!examProcessingState?.students?.length || !classListFile?.classlist?.length) {
			return;
		}

		// ---------- helpers ----------
		const digitsOnly = (s = "") => (s || "").replace(/\D+/g, "");
		const pad7 = (s = "") => s.padStart(7, "0").slice(-7); // keep last 7 after stripping (common scan issues)
		const norm = (s = "") => s.toLowerCase().trim().replace(/\s+/g, " ");
		const normNamePiece = (s = "") =>
			norm(s)
				.replace(/-/g, " ") // treat hyphen as space for equality
				.replace(/\./g, ""); // ignore periods/initial dots

		const namesEqual = (a, b) => normNamePiece(a) === normNamePiece(b);
		const oneNameMatches = (firstA, lastA, firstB, lastB) =>
			namesEqual(firstA, firstB) || namesEqual(lastA, lastB);
		// put with your helpers
		const squish = (s = "") => (s || "").toLowerCase().replace(/\s+/g, "").replace(/[-.]/g, "");

		// does idA share 5 consecutive digits with idB?
		/* const sharesFiveConsecutive = (idA, idB) => {
			if (idA.length !== 7 || idB.length !== 7) return false;
			for (let i = 0; i <= 7 - 5; i++) {
				if (idA.slice(i, i + 5) === idB.slice(i, i + 5)) return true;
			}
			return false;
		}; */

		const formatDisplayName = (first, last) => {
			const cap = (s) =>
				s
					.split(/[\s-]/)
					.filter(Boolean)
					.map((p) => p.charAt(0).toUpperCase() + p.slice(1))
					.join(s.includes("-") ? "-" : " ");
			return `${cap(last)} ${cap(first)}`;
		};

		// Parse a raw sheet name like "DOE JOHN" or "DOE, JOHN" or "John Doe" robustly.
		const parseSheetName = (raw) => {
			const s = (raw || "").trim();
			if (!s) return { first: "", last: "" };

			if (s.includes(",")) {
				// "Last, First"
				const [last, ...rest] = s.split(",");
				return { last: last.trim(), first: rest.join(",").trim() };
			}

			// No comma: try last-first (common on scans) but fall back gracefully
			const parts = s.split(/\s+/).filter(Boolean);
			if (parts.length === 1) return { first: parts[0], last: "" };
			// heuristic: if more than 2 tokens, assume first is last, last token is first (your current approach)
			return { last: parts[0], first: parts[parts.length - 1] };
		};

		// ---------- normalize LDAP list ----------
		// Assuming columns: [?, ?, ccid(2), first(3), last(4), ?, id(6)]
		const ldap = classListFile.classlist.map((row) => {
			const id = pad7(digitsOnly(row[6] ?? ""));
			const first = String(row[3] ?? "");
			const last = String(row[4] ?? "");
			return {
				raw: row,
				id,
				first,
				last,
				nFirst: normNamePiece(first),
				nLast: normNamePiece(last),
				nFull: squish(`${last}${first}`), // <— add this
				ccid: String(row[2] ?? "").trim(),
				display: formatDisplayName(first, last),
			};
		});

		// quick index by ID for exact-ID lookups
		const byId = new Map();
		for (const r of ldap) {
			if (!byId.has(r.id)) byId.set(r.id, []);
			byId.get(r.id).push(r);
		}
		const proposals = [];
		const updated = examProcessingState.students.map((db) => {
			const student = {
				...db,
				matched: false,
				match_type: "",
				matchNote: "",
				needMatching: true,
				std_name_matched: "",
				std_id_matched: null, // keep as string later
				ccid: undefined,
				candidates: [], // potential matches to show in UI if ambiguous
			};

			// clean ID as 7-char string
			const idRaw = pad7(digitsOnly(db.std_id_raw || ""));
			const { first: sheetFirstRaw, last: sheetLastRaw } = parseSheetName(db.std_name_raw || "");
			const nFirst = normNamePiece(sheetFirstRaw);
			const nLast = normNamePiece(sheetLastRaw);

			// Levenshtein distance
			const lev = (a = "", b = "") => {
				if (a === b) return 0;
				const m = a.length,
					n = b.length;
				if (m === 0) return n;
				if (n === 0) return m;
				const dp = new Array(n + 1);
				for (let j = 0; j <= n; j++) dp[j] = j;
				for (let i = 1; i <= m; i++) {
					let prev = dp[0]; // dp[i-1][j-1]
					dp[0] = i; // dp[i][0]
					for (let j = 1; j <= n; j++) {
						const tmp = dp[j]; // store dp[i-1][j] before overwrite
						const cost = a[i - 1] === b[j - 1] ? 0 : 1;
						dp[j] = Math.min(
							dp[j] + 1, // deletion
							dp[j - 1] + 1, // insertion
							prev + cost, // substitution
						);
						prev = tmp;
					}
				}
				return dp[n];
			};

			const closeEnoughName = (sheetSquished, r) => {
				// compare against both concatenations
				const fullLF = r.nFull; // last+first (you precomputed this)
				const fullFL = squish(`${r.first}${r.last}`); // first+last
				const distLF = lev(sheetSquished, fullLF);
				const distFL = lev(sheetSquished, fullFL);

				// allow up to ~10% of target length, min 1, max 2 edits
				const tolLF = Math.min(2, Math.max(1, Math.floor(fullLF.length * 0.1)));
				const tolFL = Math.min(2, Math.max(1, Math.floor(fullFL.length * 0.1)));

				return distLF <= tolLF || distFL <= tolFL;
			};

			const commit = (rec, type, note = "") => {
				student.matched = true;
				student.needMatching = false;
				student.match_type = type;
				student.matchNote = note;
				student.std_name_matched = rec.display;
				student.std_id_matched = rec.id; // keep string
				student.ccid = rec.ccid;
			};

			const setAmbiguous = (type, note, candidates) => {
				student.matched = false;
				student.needMatching = true;
				student.match_type = type;
				student.matchNote = note;
				student.candidates = candidates.map((r) => ({
					id: r.id,
					name: r.display,
					ccid: r.ccid,
				}));
			};

			// ---------- PASS 1: Exact ID + full name (or swapped) ----------
			const sheetRawName = String(db.std_name_raw || "");
			const sheetSquished = squish(sheetRawName);
			if (byId.has(idRaw)) {
				const pool = byId.get(idRaw);

				const exactNameMatches = pool.filter((r) => {
					// normal exact or swapped (already had)
					const normalExact =
						(namesEqual(nLast, r.nLast) && namesEqual(nFirst, r.nFirst)) ||
						(namesEqual(nFirst, r.nLast) && namesEqual(nLast, r.nFirst));

					if (normalExact) return true;

					// NEW: squished fallback for missing spaces/hyphens/etc.
					// e.g., "MAISODANIELLEPATRICI" should match "Maiso"+"Danielle"
					// allow prefix equality too (handles middle names/initials/truncation)
					const squishedExact =
						sheetSquished === r.nFull ||
						sheetSquished.startsWith(r.nFull) ||
						r.nFull.startsWith(sheetSquished);

					return squishedExact;
				});

				if (exactNameMatches.length === 1) {
					commit(exactNameMatches[0], "exact-id-and-name");
					proposals.push({
						rsp_id: db.rsp_id,
						targetId: returnLastFirstID(
							exactNameMatches[0].last,
							exactNameMatches[0].first,
							exactNameMatches[0].id,
						),
						match_type: "exact-id-and-name",
					});
					return student;
				}
				if (exactNameMatches.length > 1) {
					setAmbiguous(
						"exact-id-and-name",
						"Multiple LDAP entries match exact ID and name—manual disambiguation needed.",
						exactNameMatches,
					);
					return student;
				}

				const fuzzyMatches = pool.filter((r) => closeEnoughName(sheetSquished, r));
				if (fuzzyMatches.length === 1) {
					commit(
						fuzzyMatches[0],
						"exact-id-partial-name",
						"ID matches exactly; name matched via small edit-distance (likely typo or missing space).",
					);
					proposals.push({
						rsp_id: db.rsp_id,
						targetId: returnLastFirstID(
							fuzzyMatches[0].last,
							fuzzyMatches[0].first,
							fuzzyMatches[0].id,
						),
						match_type: "exact-id-partial-name",
					});
					return student;
				}
				if (fuzzyMatches.length > 1) {
					setAmbiguous(
						"exact-id-partial-name",
						"Multiple LDAP entries are close to the sheet name with exact ID—manual disambiguation needed.",
						fuzzyMatches,
					);
					return student;
				}
				const sheetFirstOrLast = [nFirst, nLast]; // both normalized already
				for (const r of pool) {
					const lastTokens = tokens(r.last); // e.g., ["reynosa","garza"]

					// Does the sheet contain LDAP first name in either position?
					const hitsFirst = sheetFirstOrLast.includes(r.nFirst);

					// Does the sheet contain ANY of the LDAP last-name tokens?
					const hitsLastToken = sheetFirstOrLast.some((t) => tokenSetHas(lastTokens, t));

					if (hitsFirst && hitsLastToken) {
						commit(
							r,
							"exact-id-last-token",
							"ID exact; first name matches and one token of the hyphenated last name matches.",
						);
						proposals.push({
							rsp_id: db.rsp_id,
							targetId: returnLastFirstID(r.last, r.first, r.id),
							match_type: "exact-id-last-token",
						});
						return student;
					}
				}

				// ---------- PASS 2: Exact ID + one name only ----------
				const oneSideMatches = pool.filter((r) => {
					const lastTokens = tokens(r.last);
					// sheet tokens
					const a = nFirst,
						b = nLast;

					return (
						// first matches directly (sheet first or last could be the first name)
						a === r.nFirst ||
						b === r.nFirst ||
						// OR sheet contains any token of LDAP last name
						lastTokens.includes(a) ||
						lastTokens.includes(b)
					);
				});

				if (oneSideMatches.length === 1) {
					commit(
						oneSideMatches[0],
						"exact-id-and-one-name",
						"First or a last-name token matches; ID matches exactly.",
					);
					proposals.push({
						rsp_id: db.rsp_id,
						targetId: returnLastFirstID(
							oneSideMatches[0].last,
							oneSideMatches[0].first,
							oneSideMatches[0].id,
						),
						match_type: "exact-id-and-one-name",
					});
					return student;
				}
				if (oneSideMatches.length > 1) {
					setAmbiguous(
						"exact-id-and-one-name",
						"Multiple LDAP entries share the ID and one-name signal—manual disambiguation needed.",
						oneSideMatches,
					);
					return student;
				}
			}

			// Build global name pools once for the remaining passes
			const exactNamePool = ldap.filter(
				(r) =>
					(namesEqual(nLast, r.nLast) && namesEqual(nFirst, r.nFirst)) ||
					(namesEqual(nFirst, r.nLast) && namesEqual(nLast, r.nFirst)),
			);

			// ---------- PASS 3: Partial ID (≥5 consecutive) + exact name ----------
			// if (idRaw && idRaw.length === 7 && exactNamePool.length) {
			// 		const partialIdHits = exactNamePool.filter((r) => sharesFiveConsecutive(idRaw, r.id));
			// 		if (partialIdHits.length === 1) {
			// 			commit(
			// 				partialIdHits[0],
			// 				"partial-id-5-and-exact-name",
			// 				"IDs share ≥5 consecutive digits; names match exactly."
			// 			);
			// 			return student;
			// 		}
			// 		if (partialIdHits.length > 1) {
			// 			setAmbiguous(
			// 				"partial-id-5-and-exact-name",
			// 				"Multiple LDAP entries share ≥5-digit run with the sheet ID and exact name—manual disambiguation needed.",
			// 				partialIdHits
			// 			);
			// 			return student;
			// 		}
			// 	}

			// ---------- PASS 4: No ID match, exact name only ----------
			// if (exactNamePool.length === 1) {
			// 		commit(exactNamePool[0], "name-only-exact", "Exact name match without ID match.");
			// 		return student;
			// 	}
			if (exactNamePool.length > 1) {
				setAmbiguous(
					"name-only-exact",
					"Multiple LDAP entries share the exact name—manual disambiguation needed.",
					exactNamePool,
				);
				return student;
			}

			// ---------- PASS 5: No ID, partial name (do NOT match; just flag) ----------
			// We can surface likely candidates (e.g., last name OR first name matches),
			// but we *do not* auto-match per your rules.
			const partialNamePool = ldap.filter((r) => oneNameMatches(nFirst, nLast, r.nFirst, r.nLast));
			if (partialNamePool.length > 0) {
				setAmbiguous(
					"no-id-partial-name",
					"Only a partial name match found; per rules, do not auto-match.",
					partialNamePool,
				);
				return student;
			}

			// ---------- PASS 6: No match at all ----------
			student.matched = false;
			student.needMatching = true;
			student.match_type = "no-match";
			student.matchNote = "No ID or name matches found. Requires manual matching.";
			return student;
		});

		const unmatched = updated.filter((s) => !s.matched);

		// Write matched/unmatched to app state
		setExamProcessingState((prev) => ({
			...prev,
			students: updated,
		}));
		setUnmatchedStudents(unmatched);

		if (AUTO_COMMIT_INITIAL) {
			// IMPORTANT: do NOT stage the ones you just committed
			setAutoState({});
			setPendingAssignments({});
		} else {
			// (Alternative mode) prefill as staged suggestions (you had this before)
			setAutoState((prev) => {
				const next = { ...prev };
				for (const { rsp_id, targetId } of proposals)
					next[rsp_id] = { value: targetId, input: targetId };
				return next;
			});
			setPendingAssignments((prev) => {
				const next = { ...prev };
				for (const { rsp_id, targetId } of proposals) next[rsp_id] = targetId;
				return next;
			});
		}

		// Optional: mark finished if everything autocommitted
		setFinishedMatching(unmatched.length === 0);

		return true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classListFile, examProcessingState]);

	// (2) Fire when the dialog opens AND data is ready, using the latest callback reference
	useEffect(() => {
		if (
			!open ||
			gotInitialMatches ||
			!hasData ||
			!classListFile ||
			classListFile.classlist.length === 0
		)
			return;
		(async () => {
			const ok = await handleMatchStudents(); // returns true if it actually ran
			if (ok) setGotInitialMatches(true);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, hasData, gotInitialMatches, classListFile]);

	const bestScoreByRspId = useMemo(() => {
		const responses = examProcessingState?.students.length > 0 ? examProcessingState.students : [];

		const remaining =
			(studentsYetToMatch && studentsYetToMatch.length
				? studentsYetToMatch
				: classListFile?.classlist) ?? [];

		// fast lookup by ID for staged choice
		const classById = new Map(classListFile?.classlist?.map((r) => [String(r[6]), r]));

		const map = {};
		const unmatched =
			responses && responses.length > 0 && responses.filter((r) => !r.matched && !r.cannot_match);

		for (const row of unmatched) {
			const id = String(row.rsp_id);
			const stagedLabel = pendingAssignments[id] || null; // <- staged choice for THIS row
			let topScore = -1,
				topBadge = "",
				topLabel = null;

			if (stagedLabel) {
				// use the score of the staged choice itself
				const tid = extractId(stagedLabel);
				const cand = classById.get(String(tid));
				if (cand) {
					const { score, badge } = scoreCandidate(row, cand);
					map[id] = { score, badge, label: stagedLabel };
					continue;
				}
				// fall through if somehow not found
			}

			// otherwise, compute best among remaining, excluding choices staged by OTHER rows
			const chosenMinusSelf = new Set(chosenIds);
			if (stagedLabel) chosenMinusSelf.delete(stagedLabel); // allow own pick

			for (const cand of remaining) {
				const label = returnLastFirstID(cand[4], cand[3], cand[6]);

				if (chosenMinusSelf.has(label)) continue;

				const { score, badge } = scoreCandidate(row, cand);

				if (score > topScore || score === -999) {
					topScore = score;
					topBadge = badge;
					topLabel = label;
				}
			}
			map[id] = { score: topScore, badge: topBadge, label: topLabel };
		}
		return map;
	}, [examProcessingState, studentsYetToMatch, classListFile, pendingAssignments, chosenIds]);

	const possibleDuplicateByRspId = useMemo(() => {
		const students = examProcessingState?.students ?? [];
		const matchedRows = students.filter((row) => row.matched && row.std_id_matched != null);
		if (matchedRows.length === 0) return {};

		const classById = new Map((classListFile?.classlist ?? []).map((row) => [String(row[6]), row]));
		const matchedCandidates = matchedRows.map((matchedRow) => {
			const matchedId = String(matchedRow.std_id_matched ?? "");
			const classRow = classById.get(matchedId);
			const displayName = classRow
				? `${classRow[4]} ${classRow[3]}`
				: String(matchedRow.std_name_matched ?? matchedRow.std_name_raw ?? "");
			const ccid = classRow ? classRow[2] : (matchedRow.ccid ?? null);
			const names = new Set(
				[
					matchedRow.std_name_matched,
					matchedRow.std_name_raw,
					classRow ? `${classRow[4]} ${classRow[3]}` : "",
					classRow ? `${classRow[3]} ${classRow[4]}` : "",
					classRow ? `${classRow[4]}, ${classRow[3]}` : "",
				]
					.map(nameKey)
					.filter(Boolean),
			);
			return {
				rsp_id: String(matchedRow.rsp_id),
				matchedId: matchedId,
				matchedIdKey: idKey(matchedRow.std_id_matched ?? matchedRow.std_id_raw),
				displayName,
				ccid,
				names,
			};
		});

		const duplicateMap = {};
		const unmatchedRows = students.filter((row) => !row.matched);
		for (const unmatchedRow of unmatchedRows) {
			const rowRspId = String(unmatchedRow.rsp_id);
			const rowId = idKey(unmatchedRow.std_id_raw ?? unmatchedRow.std_id_matched);
			const rowName = nameKey(unmatchedRow.std_name_raw ?? unmatchedRow.std_name_matched);

			let best = null;
			for (const candidate of matchedCandidates) {
				const idMatch = Boolean(rowId && candidate.matchedIdKey && rowId === candidate.matchedIdKey);
				const nameMatch = Boolean(rowName && candidate.names.has(rowName));
				if (!idMatch && !nameMatch) continue;

				const rank = idMatch && nameMatch ? 3 : idMatch ? 2 : 1;
				if (!best || rank > best.rank) {
					best = {
						...candidate,
						idMatch,
						nameMatch,
						rank,
					};
				}
				if (best?.rank === 3) break;
			}

			if (!best) continue;
			duplicateMap[rowRspId] = {
				existingRspId: best.rsp_id,
				matchedId: best.matchedId,
				matchedName: best.displayName,
				ccid: best.ccid,
				reason: best.idMatch && best.nameMatch
					? "ID and name already matched on another scanned sheet."
					: best.idMatch
						? "ID is already matched on another scanned sheet."
						: "Name is already matched on another scanned sheet.",
			};
		}

		return duplicateMap;
	}, [examProcessingState, classListFile]);

	const unmatchedRows = useMemo(() => {
		const base = examProcessingState?.students?.filter((r) => !r.matched) ?? [];

		const withScores = base.map((r) => {
			const id = String(r.rsp_id);
			const best = bestScoreByRspId[id] || {
				score: -1,
				badge: "",
				label: null,
			};

			const scoreForSort = best.score; // ignore cannot-match for sorting

			return {
				...r,
				_bestScore: scoreForSort,
				_bestCandidate: best.label,
				_bestBadge: best.badge,
			};
		});

		// Pre-sort to match the grid sort model
		withScores.sort((a, b) => {
			const d = (b._bestScore ?? -1) - (a._bestScore ?? -1);
			if (d !== 0) return d;
			return String(a.std_name_raw ?? "").localeCompare(String(b.std_name_raw ?? ""));
		});

		return withScores;
	}, [examProcessingState, bestScoreByRspId]);

	const makeOptionsForRow = (rowId) => {
		const current = pendingAssignments[rowId] ? String(pendingAssignments[rowId]) : null;
		const remaining = (studentsYetToMatch ?? classListFile?.classlist) // choose which pool you want
			.map((r) => {
				const label = returnLastFirstID(r[4], r[3], r[6]);
				return { r, label };
			})
			.filter(({ label }) => !chosenIds.has(label) || label === current)
			.map(({ r, label }) => {
				const row = examProcessingState.students.find((x) => String(x.rsp_id) === String(rowId));
				const { score, badge } = scoreCandidate(row, r);
				return { label, score, badge };
			})
			.sort((a, b) => b.score - a.score);

		return remaining;
	};

	const handleChangeAutocomplete = (_event, newValue, params) => {
		const rowId = String(params.id); // rsp_id
		// newValue is the option’s display string (or null)
		setRowAuto(rowId, { value: newValue ?? null });
		setPendingAssignments((prev) => {
			const next = { ...prev };
			if (!newValue) delete next[rowId];
			else next[rowId] = newValue; // we’ll parse the (ID) on apply
			return next;
		});
	};

	const renderSelectStudent = (params) => {
		const { value, input } = getRowAuto(params.id); // params.id == rsp_id because of getRowId

		return (
			<Autocomplete
				key={params.id}
				disabled={stagedCannotMatch[String(params.id)]}
				size="small"
				value={value} // still a string label, we’ll adapt:
				inputValue={input}
				onChange={(_e, newOpt) => {
					const label = typeof newOpt === "string" ? newOpt : (newOpt?.label ?? null);
					handleChangeAutocomplete(_e, label, params);
				}}
				onInputChange={(e, s) => setRowAuto(params.id, { input: s ?? "" })}
				options={makeOptionsForRow(params.id)}
				getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
				isOptionEqualToValue={(opt, val) => {
					const o = typeof opt === "string" ? opt : opt.label;
					const v = typeof val === "string" ? val : val?.label;
					return String(o) === String(v);
				}}
				ListboxProps={{
					style: { maxHeight: 300, overflow: "auto" }, // ~3 items visible
				}}
				renderValue={(selected) => {
					const label = typeof selected === "string" ? selected : selected.label;
					const badge = typeof selected === "string" ? "" : selected.badge;
					return (
						<div className="flex items-center ">
							<span className="text-sm">{label}</span>
							{badge && (
								<span className="ml-2 rounded px-1 py-[2px] text-[11px] bg-neutral-200">
									{badge}
								</span>
							)}
						</div>
					);
				}}
				renderOption={(props, opt) => {
					const { key, ...optionProps } = props; // keep MUI's handlers/ids
					const label = typeof opt === "string" ? opt : opt.label;
					const badge = typeof opt === "string" ? "" : opt.badge;
					const score = typeof opt === "string" ? 0 : opt.score;
					const match = label?.match(/^(.*)\(([^)]+)\)$/);
const name = match?.[1]?.trim();
const empid = match?.[2];
					return (
						<li
							className="text-sm text-black p-1 hover:bg-gray-200 hover:cursor-pointer"
							{...optionProps}
							key={key}
						>
							<div className=" items-center  w-full  ">
								{name && empid ?  (<div className="w-full flex justify-between">
    <span>{name}</span>
    <span>({empid})</span>
  </div>
) : (
  <span className="w-full">{label}</span>
)}
								
								{badge && (
									<span
										className={`ml-2 rounded px-1 py-[2px] text-[11px] ${
											score >= 85 ? "bg-primary text-white" : "bg-neutral-200"
										}`}
									>
										{badge}
									</span>
								)}
							</div>
						</li>
					);
				}}
				renderInput={(p) => <TextField {...p} variant="standard" size="small" />}
			/>
		);
	};

	const rendermatch_type = (params) => {
		const { match_type } = params.row;
		const typeMap = {
			"exact-id-and-one-name": {
				label: "Exact ID, Partial Name",
				color: "secondaryIndigo",
			},
			"exact-id-and-name": { label: "100% Match", color: "primary" },
			"exact-id-partial-name": {
				label: "Exact ID, Partial Name",
				color: "secondaryIndigo",
			},
			manual: { label: "Manually Matched", color: "secondaryGold" },
			"exact-id-last-token": {
				label: "Exact ID, Partial Name",
				color: "secondaryIndigo",
			},
			"manual-replace-existing": {
				label: "Replaced Existing Match",
				color: "secondaryGold",
			},
			"duplicate-replaced": {
				label: "Moved to Duplicate",
				color: "warning.main",
			},
		};
		return (
			<Typography
				variant="body2"
				color={typeMap[match_type]?.color ?? "text.secondary"}
				className="flex  items-center h-9"
			>
				{typeMap[match_type]?.label ?? "Unknown Match Type"}
			</Typography>
		);
	};

	const renderConfirmUnmatched = (params) => {
		const id = params.id;
		const staged = !!stagedCannotMatch[String(id)];
		return (
			<Button
				color={staged ? "inherit" : "error"}
				onClick={() => toggleCannotMatch(id)}
				disabled={params.row.matched}
				sx={{
					"&:hover": {
						textDecoration: "underline",
					},
				}}
				size="small"
			>
				{staged ? "Undo Cannot Match" : "Cannot Match"}
			</Button>
		);
	};

	const renderDuplicateIndicator = (params) => {
		const duplicate = possibleDuplicateByRspId[String(params.id)];
		if (!duplicate) return null;
		const assignedTo = `${duplicate.matchedName} (${duplicate.matchedId})`;
		return (
			<Typography
				variant="caption"
				color="warning.main"
				title={`${duplicate.reason} Currently assigned to ${assignedTo}`}
			>
				{`Possible duplicate: ${assignedTo}`}
			</Typography>
		);
	};

	const renderReplaceExistingMatch = (params) => {
		const duplicate = possibleDuplicateByRspId[String(params.id)];
		if (!duplicate || params.row.matched) return null;
		return (
			<Button
				color="warning"
				size="small"
				onClick={() => handleReplaceExistingMatch(params.id)}
				sx={{
					"&:hover": {
						textDecoration: "underline",
					},
				}}
			>
				Replace Existing Match
			</Button>
		);
	};

	const columnsMatched = [
		{
			field: "std_name_raw",
			headerName: "Raw Name",
			width: 200,
			align: "",
		},
		{
			field: "std_name_matched",
			headerName: "Matched Name",
			width: 200,
		},
		{
			field: "std_id_raw",
			headerName: "Raw ID",
			width: 100,
		},

		{ field: "std_id_matched", headerName: "Matched ID", width: 100 },
		{
			field: "ccid",
			headerName: "CCID",
			width: 120,
		},
		{
			field: "match_type",
			headerName: "Match Type",
			width: 90,
			flex: 1,
			renderCell: rendermatch_type,
		},
	];

	const columnsUnmatched = [
		{
			field: "_bestScore",
			headerName: "Match score",
			sortComparator: (v1, v2) => Number(v1 ?? -1) - Number(v2 ?? -1),
		},
		{
			field: "std_id_raw",
			headerName: "Raw ID",
			width: 100,
		},
		{
			field: "std_name_raw",
			headerName: "Raw Name",
			width: 200,
			align: "",
		},
		{
			field: "suggested",
			headerName: "Suggested Match",
			flex: 0.33,
			valueGetter: (params) => params?.row?._bestCandidate || "",
			renderCell: (params) => {
				const badge = params?.row?._bestBadge;
				const label = params?.row?._bestCandidate;
				const score = params?.row?._bestScore;
				const duplicate = possibleDuplicateByRspId[String(params.id)];
				if (score === -999) {
					return (
						<div className="text-secondaryRed">
							<span className="text-xs">NO NAME OR ID</span>
						</div>
					);
				} else if (!label || !score || !badge) {
					if (duplicate) {
						return (
							<Typography variant="caption" color="warning.main">
								{`Already matched to ${duplicate.matchedName} (${duplicate.matchedId})`}
							</Typography>
						);
					}
					return (
						<div className="text-secondaryRed">
							<span className="text-xs">No Suggestion Found</span>
						</div>
					);
				}
				return (
					<div className="flex items-center h-10">
						<span className="text-xs">{label}</span>
						{badge && (
							<span className="ml-2 rounded px-1  text-[10px] bg-primary text-white h-5 flex items-center">
								{badge}
							</span>
						)}
					</div>
				);
			},
		},
		{
			field: "duplicateCheck",
			headerName: "Duplicate Check",
			flex: 0.33,
			sortable: false,
			filterable: false,
			renderCell: renderDuplicateIndicator,
		},

		{
			field: "select",
			headerName: "Select Student",
			flex: 0.6,
			renderCell: renderSelectStudent,
		},
		{
			field: "replaceDuplicate",
			headerName: "",
			align: "center",
			flex: 0.24,
			sortable: false,
			filterable: false,
			renderCell: renderReplaceExistingMatch,
		},
		{
			field: "confirmUnmatched",
			headerName: "",
			align: "center",
			flex: 0.16,
			renderCell: renderConfirmUnmatched,
		},
	];

	const TextInput = forwardRef(function TextInput(props, ref) {
		return (
			<input
				ref={ref}
				{...props}
				className={clsx(
					"h-9 w-full rounded border border-neutral-200  px-2.5 text-base text-neutral-900  focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary",
					props.className,
				)}
			/>
		);
	});

	const toggleCannotMatch = (rspId) => {
		setStagedCannotMatch((prev) => {
			const id = String(rspId);
			const next = { ...prev };
			if (next[id]) delete next[id];
			else next[id] = true;
			// if you mark cannot-match, clear any staged selection for this row
			setPendingAssignments((p) => {
				const np = { ...p };
				delete np[id];
				return np;
			});
			// also clear the row’s input/value UI state
			setRowAuto(id, { value: null, input: "", match_type: null });
			return next;
		});
	};

	const handleReplaceExistingMatch = (rspId) => {
		const targetRspId = String(rspId);
		const duplicate = possibleDuplicateByRspId[targetRspId];
		if (!duplicate?.existingRspId || !duplicate?.matchedId) return;

		const existingRspId = String(duplicate.existingRspId);
		const classRow = (classListFile?.classlist ?? []).find(
			(row) => String(row[6]) === String(duplicate.matchedId),
		);
		const matchedId = classRow ? String(classRow[6]) : String(duplicate.matchedId);
		const matchedName = classRow
			? `${classRow[4]} ${classRow[3]}`
			: String(duplicate.matchedName ?? "");
		const ccid = classRow ? classRow[2] : (duplicate.ccid ?? null);

		const updated = (examProcessingState?.students ?? []).map((response) => {
			const currentRspId = String(response.rsp_id);
			if (currentRspId === targetRspId) {
				return {
					...response,
					matched: true,
					needMatching: false,
					cannot_match: false,
					match_type: "manual-replace-existing",
					matchNote: `Replaced existing match from scanner row ${existingRspId}.`,
					std_name_matched: matchedName,
					std_id_matched: matchedId,
					ccid,
				};
			}
			if (currentRspId === existingRspId) {
				return {
					...response,
					matched: false,
					needMatching: true,
					cannot_match: false,
					match_type: "duplicate-replaced",
					matchNote: `Match moved to scanner row ${targetRspId}.`,
					std_name_matched: "",
					std_id_matched: null,
					ccid: null,
				};
			}
			return response;
		});

		setExamProcessingState((prev) =>
			prev
				? {
						...prev,
						students: updated,
					}
				: prev,
		);

		setPendingAssignments((prev) => {
			const next = { ...prev };
			delete next[targetRspId];
			delete next[existingRspId];
			return next;
		});
		setStagedCannotMatch((prev) => {
			const next = { ...prev };
			delete next[targetRspId];
			delete next[existingRspId];
			return next;
		});
		setAutoState((prev) => {
			const next = { ...prev };
			delete next[targetRspId];
			delete next[existingRspId];
			return next;
		});

		setUnmatchedStudents(updated.filter((student) => !student.matched));
		setFinishedMatching(updated.every((student) => student.matched || student.cannot_match));
	};

	const handleSaveChanges = () => {
		// Gather staged target IDs
		const stagedPairs = Object.entries(pendingAssignments)
			.map(([rspId, label]) => ({ rspId, targetId: extractId(label) }))
			.filter((x) => !!x.targetId);

		// 1) conflicts: (a) duplicate staged targetId, (b) staged targetId already used by a matched row
		const stagedCounts = stagedPairs.reduce((acc, x) => {
			acc[x.targetId] = (acc[x.targetId] ?? 0) + 1;
			return acc;
		}, {});
		const dupStaged = Object.entries(stagedCounts)
			.filter(([, c]) => c > 1)
			.map(([id]) => id);

		const alreadyUsedIds = new Set(
			examProcessingState.students
				.filter((r) => r.matched && r.std_id_matched != null)
				.map((r) => String(r.std_id_matched)),
		);
		const clashesWithExisting = stagedPairs
			.filter((x) => alreadyUsedIds.has(String(x.targetId)))
			.map((x) => x.targetId);

		if (dupStaged.length || clashesWithExisting.length) {
			const msgs = [];
			if (dupStaged.length) msgs.push(`Duplicate assignment(s): ${dupStaged.join(", ")}`);
			if (clashesWithExisting.length)
				msgs.push(`Conflicts with already matched: ${clashesWithExisting.join(", ")}`);
			alert(`Resolve before applying:\n• ${msgs.join("\n• ")}`);
			return;
		}

		// 2) Build quick lookup of classlist rows by ID for committing fields
		const classById = new Map(classListFile.classlist.map((r) => [String(r[6]), r]));

		// 3) Commit
		const updated = examProcessingState.students.map((resp) => {
			const id = String(resp.rsp_id);

			// staged "cannot match"
			if (stagedCannotMatch[id]) {
				return {
					...resp,

					matched: false,
					cannot_match: true,
					match_type: "cannot-match",
					matchNote: "Confirmed cannot match",
				};
			}

			// staged manual match
			const staged = stagedPairs.find((p) => p.rspId === id);
			if (staged) {
				const row = classById.get(String(staged.targetId));
				// row columns: ccid=2, first=3, last=4, id=6 in your code comments
				const matchedName = row ? `${row[4]} ${row[3]}` : null;
				const matchedId = row ? row[6] : null;
				const ccid = row ? row[2] : null;
				return {
					...resp,
					matched: true,
					needMatching: false,
					match_type: "manual",
					matchNote: "Manually matched by user (staged apply)",
					std_name_matched: matchedName,
					std_id_matched: matchedId,
					ccid: ccid,
					cannot_match: false,
				};
			}

			// untouched
			return resp;
		});

		// 4) Write once
		setExamProcessingState((prev) => ({
			...prev,
			students: updated,
		}));

		// 5) Clear staging + recompute “finished”
		setPendingAssignments({});
		/* setStagedCannotMatch({}); */
		setAutoState({});
		const allDone = updated.every((r) => r.matched || r.cannot_match);
		setFinishedMatching(allDone);

		// 6) If you maintain a separate unmatched list, recompute it
		const unmatched = updated.filter((s) => !s.matched);
		setUnmatchedStudents(unmatched);
	};

	const CustomToolbar = () => {
		return (
			<Toolbar className="gap-2! p-2!">
				<QuickFilter
					render={(props, state) => (
						<div {...props} className="mr-auto flex overflow-clip">
							<QuickFilterTrigger
								className={state.expanded ? "rounded-r-none border-r-0" : ""}
								render={
									<ToolbarButton
										render={
											<Button aria-label="Search">
												<SearchIcon fontSize="small" />
											</Button>
										}
									/>
								}
							/>
							<div
								className={clsx(
									"flex overflow-clip transition-all duration-300 ease-in-out",
									state.expanded ? "w-48" : "w-0",
								)}
							>
								<QuickFilterControl
									aria-label="Search"
									placeholder="Search"
									render={({ slotProps, size, ...controlProps }) => (
										<TextInput
											{...controlProps}
											{...slotProps?.htmlInput}
											className={clsx(
												"flex-1 rounded-l-none",
												state.expanded && state.value !== "" && "rounded-r-none",
											)}
										/>
									)}
								/>
								{state.expanded && state.value !== "" && (
									<QuickFilterClear
										render={
											<Button aria-label="Clear" className="rounded-l-none">
												<CancelIcon fontSize="small" />
											</Button>
										}
									/>
								)}
							</div>
						</div>
					)}
				/>
			</Toolbar>
		);
	};

	const onFinishAndExport = async () => {
		try {
			await handleUpdateStudentsInDB(); // your existing save
			if (exportTXTLog || exportCSVLog) {
				exportLogsOnFinish({ examProcessingState, classListFile });
			}
			setOpen(false); // close dialog
		} catch (e) {
			console.error(e);
			// optional: toast error
		}
	};
	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					handleClose();
				}
			}}
			aria-labelledby="unmatched-student-dialog-title"
			fullScreen
			maxWidth={false}
		>
			<DialogTitle id="unmatched-student-dialog-title" className="bg-digitalWhite">
				{finishedMatching ? "All Students Matched" : `Manual Student Matching Required `}
			</DialogTitle>
			<DialogContent className="bg-digitalWhite">
				<Accordion className="max-h-[70dvh]" expanded={!isExpanded}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel1-content"
						id="panel1-header"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<Typography component="span">
							Matched Students ({examProcessingState?.students.filter((r) => r.matched).length})
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Alert severity="success" sx={{ mb: 2 }}>
							{finishedMatching
								? "All students have been matched. Please click 'Finish Matching and Save' to continue."
								: "The following students were successfully matched:"}
						</Alert>
						<div className="flex gap-2 h-[50dvh]">
							<DataGrid
								sx={{
									"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
										outline: "None !important",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
										width: "0.4em",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
										background: "#f1f1f1",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
										backgroundColor: "#888",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
										background: "#555",
									},

									borderRadius: "0px 4px 4px 0px",
								}}
								pagination
								disableSelectionOnClick
								initialState={{
									sorting: {
										sortModel: [
											{ field: "match_type", sort: "desc" }, // stable secondary
										],
									},
									pagination: {
										paginationModel: { pageSize: 100, page: 0 },
									},
								}}
								slots={{
									/* footer: CustomFooter, */
									toolbar: CustomToolbar,
								}}
								rows={examProcessingState?.students.filter((response) => response.matched)}
								density="compact"
								columns={columnsMatched}
								getRowId={(row) => row.rsp_id}
							/>
						</div>
					</AccordionDetails>
				</Accordion>
				<Accordion className="max-h-[80dvh]" expanded={isExpanded}>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="panel2-content"
						id="panel2-header"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<Typography component="span">
							Unmatched Students ({examProcessingState?.students.filter((r) => !r.matched).length})
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Alert severity={hasUnmatched ? "error" : "success"} sx={{ mb: 2 }}>
							{hasUnmatched
								? "The following students were not matched to the class list. Please match them manually, or confirm that they cannot be matched."
								: "All students have been matched. Please click 'Finish Matching and Save' to continue."}
						</Alert>
						<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
							<Chip
								label={`Matched: ${
									examProcessingState?.students.filter((s) => s.matched).length ?? 0
								}`}
								color="primary"
								variant="filled"
							/>
							<Chip
								label={`Unmatched: ${
									examProcessingState?.students.filter((s) => !s.matched && !s.cannot_match)
										.length ?? 0
								}`}
								color="error"
								variant="filled"
							/>
							<Chip
								label={`Cannot Match: ${
									examProcessingState?.students.filter((s) => s.cannot_match).length ?? 0
								}`}
								color="default"
								variant="outlined"
							/>
							<Chip
								label={`Total: ${examProcessingState?.students.length ?? 0}`}
								color="primary"
								variant="outlined"
							/>
						</Stack>
						<div className="flex gap-2 h-[55dvh]">
							<DataGrid
								sx={{
									"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
										outline: "None !important",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
										width: "0.4em",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
										background: "#f1f1f1",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
										backgroundColor: "#888",
									},
									"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
										background: "#555",
									},

									borderRadius: "0px 4px 4px 0px",
								}}
								pagination
								disableSelectionOnClick
								columnVisibilityModel={{
									_bestScore: false,
									std_id_raw: true,
									std_name_raw: true,
									confirmUnmatched: true,
									select: true,
								}}
								initialState={{
									sorting: {
										sortModel: [{ field: "std_name_raw", sort: "asc" }],
									},
									pagination: {
										paginationModel: { pageSize: 100, page: 0 },
									},
								}}
								slots={{
									toolbar: CustomToolbar,
								}}
								rows={unmatchedRows}
								density="compact"
								columns={columnsUnmatched}
								getRowId={(row) => row.rsp_id}
							/>
						</div>
					</AccordionDetails>
				</Accordion>
			</DialogContent>

			{finishedMatching ? (
				<DialogActions>
					<div className="flex w-full justify-between gap-2">
						<Button onClick={handleClose} disabled={loadingUpdateStudentRecords}>
							Abort Matching
						</Button>
						<Button
							onClick={onFinishAndExport}
							color="primary"
							disabled={loadingUpdateStudentRecords || hasStagedChanges}
							variant="contained"
							title={hasStagedChanges ? "Apply or discard staged changes first" : undefined}
						>
							{loadingUpdateStudentRecords ? (
								<CircularProgress size={24} color="white" />
							) : (
								"Finish Matching and Save"
							)}
						</Button>
						{/* </div> */}
					</div>
				</DialogActions>
			) : (
				<DialogActions>
					<div className="flex w-full justify-between items-center">
						<Button onClick={handleWrongClassId}>Wrong Class?</Button>

						<div>
							<Button onClick={handleClose}>Abort Matching</Button>
							<Button
								onClick={handleSaveChanges}
								color="primary"
								variant="contained"
								disabled={(!hasStagedChanges && !hasStagedCannotMatch) || !canApplyChanges}
							>
								{hasStagedChanges || hasStagedCannotMatch ? "Apply Changes" : "No Changes"}
							</Button>
						</div>
					</div>
				</DialogActions>
			)}
		</Dialog>
	);
}
